import React, { Fragment } from 'react';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { Box } from '@mui/system';
import { Link } from 'react-router-dom';
import { routerUrls, currency } from 'constants';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import createGAevent from 'helpers';
import PriceRangeLine from './PriceRangeLine';
import { isSateen300 } from '../utils/productCodes';
import fs from 'fs';
import PropTypes from 'prop-types';

// Per-material tag palette: sateen gets a distinct cool slate tone so it reads apart from the
// warm-gold default used by ranforce and the other materials. Matches on the Cyrillic name so it
// works regardless of the "100% памук - " prefix.
const materialChipColors = (name) => {
  if ((name || '').toLowerCase().includes('сатен')) {
    return { color: '#1f4a63', backgroundColor: '#e8f1f6', border: '1px solid #b3d1e0' };
  }
  return { color: '#5a4a1f', backgroundColor: '#faf5e6', border: '1px solid #e0cf92' };
};

// Premium 300-thread-count sateen, identified by the thread-count suffix on the product code (e.g.
// "S-891-300tc"). These carry the standard sateen material in the DB, so they'd otherwise show the
// generic sateen tag; flag them apart on the landing grid with their own label + plum color.
const SATEEN_300_TAG = {
  label: '100% памук - САТЕН 300 ТС',
  colors: { color: '#5b2a4d', backgroundColor: '#f6eaf2', border: '1px solid #dcc0d3' }
};

// Shared pill geometry for the tags under the image.
const chipSx = {
  height: 22,
  maxWidth: '100%',
  fontSize: { xs: '0.62rem', md: '0.68rem' },
  fontWeight: 600,
  letterSpacing: 0.2,
  borderRadius: '999px',
  '& .MuiChip-label': {
    px: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
};

// The product-type tag stays neutral so it never competes with the material tag,
// which carries the per-material colour coding.
const typeChipColors = {
  color: 'text.secondary',
  backgroundColor: 'background.default',
  border: '1px solid',
  borderColor: 'hairline.strong'
};

// TODO: LOSALISE COMPONENT
// GOTOBED PRODUCT COMPONENTS
// `materialName`, when provided, renders a small material tag under the image. It is passed
// throughout the category grid - both the mixed-material landing and the single-material views, so
// the tag doesn't vanish the moment a material chip is picked - see Products.jsx. Search results
// and related carousels don't pass it and stay unchanged.
// `compact` shrinks the card for the related-products rail, where a full-size 4/5 card is far too tall.
const Product = ({ Product, attributeQuery, materialName, productTypeName, compact = false }) => {
  const params = useParams();
  const [priceRange, setPriceRange] = useState([]);

  // The hover photo. Products with a single image (or a duplicate second one) get no swap, so the
  // card simply doesn't change on hover rather than fading to an identical picture.
  const images = Product?.images || [];
  const secondImageUrl =
    images.length > 1 && images[1].url !== images[0].url ? images[1].url : null;

  // Resolve the material tag: a 300 TC sateen code override wins over the generic material name/color.
  const tagOverride = isSateen300(Product.code) ? SATEEN_300_TAG : null;
  const materialTagLabel = tagOverride ? tagOverride.label : materialName;
  const materialTagColors = tagOverride ? tagOverride.colors : materialChipColors(materialName);

  // Price type scale. The card stays quiet, while the currency code steps down to roughly two
  // thirds of the amount so the number reads as the price and the code as its label.
  const priceAmountSx = {
    color: 'text.secondary',
    fontWeight: 400,
    fontSize: compact ? 13.5 : 16,
    lineHeight: 1.3
  };
  const priceCurrencySx = { fontSize: compact ? 9 : 10.5, letterSpacing: '0.04em' };

  const fetchPriceRange = async (productId) => {
    try {
      params['productId'] = productId;
      const { data: response } = await axios.get(`/price/`, {
        params
      });

      let priceRange = [];
      let minPrice = null;
      let maxPrice = null;

      if (response && response.length > 1) {
        minPrice = Math.min.apply(
          null,
          response.map((obj) => parseFloat(obj.price_eur))
        );
        priceRange.push(minPrice);

        maxPrice = Math.max.apply(
          null,
          response.map((obj) => parseFloat(obj.price_eur))
        );
        priceRange.push(maxPrice);
        return priceRange;
      } else if (response.length === 1) {
        minPrice = parseFloat(response[0].price_eur);
        priceRange.push(minPrice);
        return priceRange;
      } else {
        return priceRange;
      }
    } catch (error) {
      let errorMessage = {
        error:
          '--------------------------------------------------------------------------' +
          new Date() +
          '-  Error while trying to fetchPriceRange!!!',
        error_log: error
      };
      fs.appendFile(
        'error_logs.json',
        JSON.stringify(errorMessage) +
          '--------------------------------------------------------------------------------------------------------------'
      );
      return []; // Return empty array on error instead of undefined
    }
  };

  const getPriceRange = async (productId) => {
    const priceRangeData = await fetchPriceRange(productId);
    setPriceRange(priceRangeData);
  };

  useEffect(() => {
    getPriceRange(Product.id);
    createGAevent('ProductDetails', `PRODUCT_DETAILS-id-${Product.id}`, Product.id);
  }, []);

  return (
    //TODO: localise slug!!!!
    <Link
      to={routerUrls.product + '/' + Product.slug_bg + '/' + Product.id + (attributeQuery || '')} //here we do the connection between ProductView and Product; attributeQuery preselects a dimension on the detail page
      state={{ Product, priceRange }}
      style={{ color: 'inherit', textDecoration: 'none' }}>
      <Fragment>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            backgroundColor: 'background.default',
            overflow: 'hidden',
            transition: 'transform 0.25s ease',
            '& img': { transition: 'transform 0.4s ease, opacity 0.4s ease' },
            '&:hover': {
              transform: 'translateY(-3px)',
              '& img': { transform: 'scale(1.03)' },
              // Fade the second photo in over the first. Both are already in the DOM, so there
              // is no flash of an unloaded image the way swapping `src` on mouseover would give.
              // Only the top image fades - cross-fading both would wash out at the midpoint.
              '& .card-img-secondary': { opacity: 1 }
            },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
              '& img': { transition: 'none' },
              '&:hover': { transform: 'none', '& img': { transform: 'none' } }
            }
          }}>
          {/* Responsive image frame - scales with the grid column; squarer when compact */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: compact ? '1 / 1' : '4 / 5',
              backgroundColor: 'background.paper',
              overflow: 'hidden',
              '& .card-img': {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              },
              // Hover photos are shot at a different aspect ratio than the primary (often 2:3
              // against the frame's 4:5), so `contain` would letterbox them with a gutter down
              // each side. Scale to fill the frame instead; the crop is off the top and bottom.
              '& .card-img-secondary': { objectFit: 'cover' }
            }}>
            <Box
              component="img"
              className="card-img card-img-primary"
              src={Product?.images?.length > 0 ? Product.images[0].url : 'some_default_img'}
              alt={Product.name_bg}
            />
            {/* The second photo sits underneath, revealed on hover. Empty alt: it shows the same
                product, so a screen reader announcing it twice would be noise. */}
            {secondImageUrl && (
              <Box
                component="img"
                className="card-img card-img-secondary"
                src={secondImageUrl}
                alt=""
                aria-hidden="true"
                loading="lazy"
                sx={{ opacity: 0 }}
              />
            )}
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              p: compact ? 1 : { xs: 1.25, md: 1.75 }
            }}>
            {/* Tags: material (category grid), product type (related-products rail) */}
            {(materialName || productTypeName) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  mb: 1
                }}>
                {materialName && (
                  <Chip
                    label={materialTagLabel}
                    size="small"
                    sx={{ ...chipSx, ...materialTagColors }}
                  />
                )}
                {productTypeName && (
                  <Chip
                    label={productTypeName}
                    size="small"
                    sx={{ ...chipSx, ...typeChipColors }}
                  />
                )}
              </Box>
            )}

            {/* Title */}
            <Typography
              gutterBottom
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                fontWeight: 400,
                fontSize: compact ? 12.5 : { xs: 14, md: 15.5 },
                lineHeight: 1.35,
                color: 'text.primary'
              }}>
              {Product.name_bg}
            </Typography>

            {/* Price - amount leads, currency code a step under it, as on the product page */}
            {priceRange.length > 0 && (
              // The dash between the two ends of a range inherits its size from here, so it tracks
              // the amounts rather than the card's base text.
              <Box sx={{ mt: 'auto', pt: 0.5, fontSize: priceAmountSx.fontSize }}>
                <PriceRangeLine
                  amounts={priceRange.map((price) => parseFloat(price).toFixed(2))}
                  code={currency.EUR}
                  amountSx={priceAmountSx}
                  currencySx={priceCurrencySx}
                  justifyContent="center"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Fragment>
    </Link>
  );
};

Product.propTypes = {
  attributeQuery: PropTypes.string,
  materialName: PropTypes.string,
  productTypeName: PropTypes.string,
  compact: PropTypes.bool,
  Product: PropTypes.shape({
    id: PropTypes.number.isRequired,
    code: PropTypes.string,
    slug_bg: PropTypes.string.isRequired,
    name_bg: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired
};

export default Product;
